import React, { useEffect, useState } from 'react';
import {
  useHMSActions,
  useHMSStore,
  selectIsConnectedToRoom,
} from '@100mslive/react-sdk';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ensureAuthorized, redirectToAuthorize } from '../../lib/ringcentral/auth';

import { useUser } from '../../contexts/UserContext';
import { useCreateCall } from '../../services/calls';
import useEffectOnce from '../../hooks/useEffectOnce';
import PrivateLayout from '../layouts/PrivateLayout';
import { Button } from 'react-bootstrap';
import Loading from '../common/Loading';

import InConference from './components/InConference';
import PreviewConference from './components/PreviewConference';

const Call = () => {
  const { user } = useUser();
  const { inviteId } = useParams();
  const [data, setData] = useState(null);
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const navigate = useNavigate();
  const [rcMeetingUrl, setRcMeetingUrl] = useState(null);

  const joinConference = async () => {
    await hmsActions.join({
      userName: `${user.userFirstName} ${user.userLastName}`,
      authToken: data.authToken,
      settings: {
        isAudioMuted: false,
        isVideoMuted: false,
      },
      rememberDeviceSelection: true,
    });
  };

  const call = useCreateCall(
    async (res) => {
      if (res && res.ringCentralUrl) {
        // If RingCentral meeting, ensure RC auth so user joins as themselves
        const ok = await ensureAuthorized();
        if (!ok) {
          redirectToAuthorize();
          return;
        }
        // Open RingCentral in a new tab and show message here
        window.open(res.ringCentralUrl, '_blank', 'noopener,noreferrer');
        setRcMeetingUrl(res.ringCentralUrl);
        return;
      }
      // use room code to fetch auth token (100ms)
      const authToken = await hmsActions.getAuthTokenByRoomCode({
        roomCode: res.roomCode,
      });

      setData({ ...res, authToken });
    },
    {
      onError: (err) => {
        if (err?.response?.status === 428) {
          redirectToAuthorize();
        } else {
          toast.error(err.message);
        }
      },
    },
  );

  const leaveConference = async () => {
    await hmsActions.leave();
    navigate('/dashboard');
  };

  useEffect(() => {
    window.onunload = () => {
      if (isConnected) {
        hmsActions.leave();
      }
    };
  }, [hmsActions, isConnected]);

  useEffectOnce(() => {
    call.mutate(inviteId);
  });

  const renderView = () => {
    if (rcMeetingUrl) {
      return (
        <div className="text-center p-4">
          <h3>Opening your RingCentral meeting...</h3>
          <p className="mt-3">We opened the meeting in a new tab. If it didn’t open, use the button below.</p>
          <Button
            variant="primary"
            className="mt-2"
            onClick={() => window.open(rcMeetingUrl, '_blank', 'noopener,noreferrer')}
          >
            Open RingCentral Meeting in New Tab
          </Button>
        </div>
      );
    }

    if (isConnected) {
      return <InConference onEndCall={() => leaveConference()} />;
    }

    if (data && data.roomCode) {
      return (
        <PreviewConference
          withUser={data.invite}
          authToken={data.authToken}
          onJoin={() => joinConference()}
          onLeave={() => leaveConference()}
        />
      );
    }

    return <Loading msg="Preparing your call..." />;
  };

  return <PrivateLayout>{renderView()}</PrivateLayout>;
};

export default Call;
